import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, Tag, message, Select, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CreateClubModal } from '../CreateClubModal';
import apiService from '@/services/api.service';
import type { Club, Federation as FederationModel } from '@/types/models';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const ClubsPage: React.FC = () => {
    const location = useLocation();
    const [clubs, setClubs] = useState<Club[]>([]);
    const [filteredClubs, setFilteredClubs] = useState<Club[]>([]);
    const [federations, setFederations] = useState<FederationModel[]>([]);
    const [clubStatusFilter, setClubStatusFilter] = useState<string>('all');
    const [clubFederationFilter, setClubFederationFilter] = useState<number | null>(null);
    const [isClubsLoading, setIsClubsLoading] = useState(false);
    const [isCreateClubModalVisible, setIsCreateClubModalVisible] = useState(false);
    const [selectedClub, setSelectedClub] = useState<Club | null>(null);

    const fetchClubs = async () => {
        try {
            setIsClubsLoading(true);
            const response = await apiService.get<ApiResponse<Club[]>>(API_ENDPOINTS.CLUBS.BASE);
            const clubsData = response.data?.data;
            let clubsArray: Club[] = [];
            if (Array.isArray(clubsData)) {
                clubsArray = clubsData;
            } else if (clubsData && typeof clubsData === 'object' && 'rows' in clubsData) {
                clubsArray = (clubsData as any).rows || [];
            }
            setClubs(clubsArray);
            filterClubs(clubsArray, clubStatusFilter, clubFederationFilter);
        } catch (error) {
            logger.error('Error fetching clubs:', error);
            message.error('Ошибка при загрузке клубов');
        } finally {
            setIsClubsLoading(false);
        }
    };

    const fetchFederations = async () => {
        try {
            const response = await apiService.get<ApiResponse<FederationModel[] | { items: FederationModel[] }>>(API_ENDPOINTS.FEDERATIONS.BASE, { params: { limit: 1000 } });
            const fedsData = response.data?.data;
            let fedsArray: FederationModel[] = [];
            if (Array.isArray(fedsData)) {
                fedsArray = fedsData;
            } else if (fedsData && typeof fedsData === 'object' && 'items' in fedsData) {
                fedsArray = (fedsData as any).items || [];
            }
            setFederations(fedsArray);
        } catch (error) {
            logger.error('Error fetching federations:', error);
        }
    };

    const filterClubs = (clubsList: Club[], status: string, federationId: number | null) => {
        let filtered = [...clubsList];
        if (status !== 'all') {
            filtered = filtered.filter(club => club.status === status);
        }
        if (federationId !== null) {
            if (federationId === -1) {
                // Фильтр "Без федерации"
                filtered = filtered.filter(club => !club.federationId);
            } else {
                filtered = filtered.filter(club => club.federationId === federationId);
            }
        }
        setFilteredClubs(filtered);
    };

    const handleClubStatusFilterChange = (value: string) => {
        setClubStatusFilter(value);
        filterClubs(clubs, value, clubFederationFilter);
    };

    const handleClubFederationFilterChange = (value: number | null) => {
        setClubFederationFilter(value);
        filterClubs(clubs, clubStatusFilter, value);
    };

    const handleClubCreateSuccess = () => {
        setIsCreateClubModalVisible(false);
        setSelectedClub(null);
        fetchClubs();
    };

    useEffect(() => {
        fetchFederations();
    }, []);

    useEffect(() => {
        if (location.pathname === '/admin/clubs') {
            fetchClubs();
        }
    }, [location.pathname]);

    useEffect(() => {
        filterClubs(clubs, clubStatusFilter, clubFederationFilter);
    }, [clubs, clubStatusFilter, clubFederationFilter]);

    const clubColumns: ColumnsType<Club> = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по названию"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(String(value).toLowerCase()),
        },
        {
            title: 'Город',
            dataIndex: 'city',
            key: 'city',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по городу"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.city?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Адрес',
            dataIndex: 'address',
            key: 'address',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                        placeholder="Поиск по адресу"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
                            Поиск
                        </Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
                            Сброс
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) =>
                record.address?.toLowerCase().includes(String(value).toLowerCase()) ?? false,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Активен', value: 'active' },
                { text: 'Неактивен', value: 'inactive' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Активен' : 'Неактивен'}
                </Tag>
            ),
        },
        {
            title: 'Расш. лицензия',
            dataIndex: 'extendedLicense',
            key: 'extendedLicense',
            width: 120,
            render: (extendedLicense: boolean) => (
                <Tag color={extendedLicense ? 'green' : 'default'}>
                    {extendedLicense ? 'Да' : 'Нет'}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Club) => (
                <Space>
                    <Button type="link" onClick={() => {
                        setSelectedClub(record);
                        setIsCreateClubModalVisible(true);
                    }}>
                        Редактировать
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setSelectedClub(null);
                        setIsCreateClubModalVisible(true);
                    }}
                >
                    Создать клуб
                </Button>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Статус:</span>
                        <Select
                            value={clubStatusFilter}
                            style={{ width: 120 }}
                            onChange={handleClubStatusFilterChange}
                            options={[
                                { value: 'all', label: 'Все' },
                                { value: 'active', label: 'Активные' },
                                { value: 'inactive', label: 'Неактивные' },
                            ]}
                        />
                    </div>
                    <div>
                        <span style={{ marginRight: 8 }}>Федерация:</span>
                        <Select
                            value={clubFederationFilter}
                            style={{ width: 200 }}
                            onChange={handleClubFederationFilterChange}
                            allowClear
                            placeholder="Все федерации"
                            options={[
                                { value: -1, label: 'Без федерации' },
                                ...federations.map(federation => ({
                                    value: federation.id,
                                    label: federation.name
                                }))
                            ]}
                        />
                    </div>
                </div>
            </div>
            <Table
                columns={clubColumns}
                dataSource={filteredClubs}
                rowKey="id"
                loading={isClubsLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} клубов`,
                }}
            />
            <CreateClubModal
                visible={isCreateClubModalVisible}
                club={selectedClub}
                onCancel={() => {
                    setIsCreateClubModalVisible(false);
                    setSelectedClub(null);
                }}
                onSuccess={handleClubCreateSuccess}
            />
        </div>
    );
};

export default ClubsPage;


